import { Injectable, inject } from '@angular/core';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';
import { BIZCARE_INFO_SYSTEM_PROMPT } from './bizcare-system-prompt';
import {
    AiBrainAgent,
    AiBrainAgentCreatePayload,
    AiBrainAgentUpdatePayload,
    AiBrainChatResponse,
    AiBrainIdentity,
} from './ai-brain.types';

export type AiBrainChatOptions = {
    sessionId?: string;
    systemPrompt?: string;
};

export type AiBrainAgentChatResponse = AiBrainChatResponse & {
    agentId?: string;
    tokensCharged?: number;
    tokenRemaining?: number;
    stepsUsed?: number;
};

/**
 * Calls the separate AI Brain product API.
 * Uses fetch (not Angular HttpClient) so ERP auth/idempotency interceptors never touch it.
 */
@Injectable({ providedIn: 'root' })
export class AiBrainService {
    private readonly base = (environment.aiBrainApiUrl || '').replace(/\/$/, '');
    private readonly apiKey = environment.aiBrainApiKey || '';
    private readonly _auth = inject(AuthService);

    isConfigured(): boolean {
        return Boolean(this.base && this.apiKey && this.apiKey.startsWith('aib_'));
    }

    async getIdentity(): Promise<AiBrainIdentity> {
        return this._requestJson<AiBrainIdentity>('/companies/me/identity');
    }

    async listAgents(): Promise<AiBrainAgent[]> {
        return this._requestJson<AiBrainAgent[]>('/me/agents');
    }

    async getAgent(id: string): Promise<AiBrainAgent> {
        return this._requestJson<AiBrainAgent>(`/agents/${id}`);
    }

    async createAgent(payload: AiBrainAgentCreatePayload): Promise<AiBrainAgent> {
        return this._requestJson<AiBrainAgent>('/me/agents', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async updateAgent(id: string, payload: AiBrainAgentUpdatePayload): Promise<AiBrainAgent> {
        return this._requestJson<AiBrainAgent>(`/agents/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
    }

    async deleteAgent(id: string): Promise<{ id: string; deleted: boolean }> {
        return this._requestJson(`/agents/${id}`, { method: 'DELETE' });
    }

    /** Floating BizAI assistant — product help only (no ERP tools / no custom agents). */
    async chat(message: string, options: AiBrainChatOptions | string = {}): Promise<AiBrainChatResponse> {
        const opts: AiBrainChatOptions =
            typeof options === 'string' ? { sessionId: options } : options ?? {};

        return this._requestJson<AiBrainChatResponse>('/chat', {
            method: 'POST',
            body: JSON.stringify({
                message,
                sessionId: opts.sessionId,
                systemPrompt: opts.systemPrompt || BIZCARE_INFO_SYSTEM_PROMPT,
            }),
        });
    }

    /**
     * Dedicated chat for one custom brain agent (module-scoped tools + token metering).
     * Sends the logged-in ERP user token so the agent can call BizCare APIs as that user.
     */
    async agentChat(
        agentId: string,
        message: string,
        sessionId?: string
    ): Promise<AiBrainAgentChatResponse> {
        const erpBaseUrl = (environment.apiUrl || '').trim();
        const erpAccessToken = this._auth.accessToken || '';
        if (!erpBaseUrl) {
            throw new Error('ERP apiUrl is missing in environment.');
        }
        if (!erpAccessToken) {
            throw new Error('You must be signed in to BizCare to chat with this agent.');
        }

        return this._requestJson<AiBrainAgentChatResponse>(`/agents/${agentId}/chat`, {
            method: 'POST',
            body: JSON.stringify({
                message,
                sessionId,
                erpBaseUrl,
                erpAccessToken,
            }),
        });
    }

    private async _requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
        if (!this.isConfigured()) {
            throw new Error(
                'AI Brain is not configured. Set aiBrainApiUrl and aiBrainApiKey in environment.'
            );
        }

        const res = await fetch(`${this.base}${path}`, {
            ...init,
            headers: {
                Accept: 'application/json',
                'x-api-key': this.apiKey,
                ...(init.body ? { 'Content-Type': 'application/json' } : {}),
                ...(init.headers || {}),
            },
        });

        if (!res.ok) {
            const text = await res.text().catch(() => '');
            let detail = text;
            try {
                const json = JSON.parse(text) as { message?: string | string[] };
                detail = Array.isArray(json.message)
                    ? json.message.join(', ')
                    : json.message || text;
            } catch {
                // keep raw text
            }
            throw new Error(detail || `AI Brain request failed (${res.status})`);
        }

        return (await res.json()) as T;
    }
}
